import React, { useState } from "react";
import { Minus, Plus, ShoppingCart, MapPin, Award, Tag, Check, Phone } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { fmtPrice } from "../data/site";

export const ProductCard = ({ product, settings }) => {
    const [cases, setCases] = useState(1);
    const [added, setAdded] = useState(false);
    const { addItem } = useCart();

    const showRetail = settings?.show_retail_prices !== false;

    const handleAdd = () => {
        addItem(product, cases);
        toast.success(`Added ${cases} case${cases > 1 ? "s" : ""} of ${product.name} to your order request`);
        setAdded(true);
        setTimeout(() => setAdded(false), 1500);
    };

    return (
        <div
            className="group flex flex-col bg-card border border-border hover:border-primary/60 transition-colors duration-300 shadow-sm"
            data-testid={`product-card-${product.id}`}
        >
            <div className="relative aspect-square overflow-hidden bg-muted">
                {product.image ? (
                    <img
                        src={product.image}
                        alt={product.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center font-heading font-black text-2xl text-muted-foreground">
                        {product.brand}
                    </div>
                )}
                <div className="absolute top-0 left-0 flex flex-col">
                    {product.limited_time && (
                        <span className="bg-secondary text-secondary-foreground text-[10px] font-bold uppercase tracking-wider px-3 py-1.5">
                            Limited Offer
                        </span>
                    )}
                    {!product.in_stock && (
                        <span className="bg-destructive text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5">
                            Out of Stock
                        </span>
                    )}
                </div>
                <span className="absolute top-2 right-2 bg-background/90 text-foreground text-[10px] font-semibold uppercase tracking-wider px-2 py-1">
                    {product.brand}
                </span>
            </div>

            <div className="flex flex-col flex-1 p-5">
                <h3 className="font-heading font-bold text-lg leading-tight mb-1">{product.name}</h3>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground mb-1">
                    <span className="font-semibold text-foreground">{product.brand}</span>
                    {product.category && <><span>·</span><span>{product.category}</span></>}
                </div>
                {product.unit_size && (
                    <p className="text-sm text-muted-foreground mb-3">
                        {product.unit_size} · {product.units_per_case} per case{product.packaging ? ` · ${product.packaging}` : ""}
                    </p>
                )}

                {product.flavors?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                        {product.flavors.slice(0, 4).map((f) => (
                            <span key={f} className="text-[11px] bg-muted px-2 py-0.5 text-muted-foreground">{f}</span>
                        ))}
                        {product.flavors.length > 4 && (
                            <span className="text-[11px] text-muted-foreground px-1">+{product.flavors.length - 4} more</span>
                        )}
                    </div>
                )}

                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground mb-3">
                    {product.origin && <span className="flex items-center gap-1"><MapPin size={11} /> {product.origin}</span>}
                    {product.certifications && <span className="flex items-center gap-1"><Award size={11} /> {product.certifications}</span>}
                </div>

                {product.notes && <p className="text-[11px] italic text-muted-foreground mb-3">{product.notes}</p>}

                <div className="mt-auto space-y-3 pt-3 border-t border-border">
                    <div className="min-h-[24px] flex items-baseline justify-between text-sm text-muted-foreground">
                        {showRetail && product.suggested_retail != null && (
                            <>
                                <span className="flex items-center gap-1"><Tag size={12} /> Suggested retail</span>
                                <span className="font-heading font-bold text-base text-foreground">{fmtPrice(product.suggested_retail)}</span>
                            </>
                        )}
                    </div>
                    <a
                        href="tel:360-583-7945"
                        data-testid={`call-for-price-${product.id}`}
                        className="flex items-center justify-center gap-2 w-full border border-primary text-primary font-semibold py-2.5 text-sm hover:bg-primary hover:text-primary-foreground transition-colors"
                    >
                        <Phone size={15} /> Call for Price
                    </a>

                    <div className="flex items-stretch gap-2">
                        <div className="flex items-center border border-border">
                            <button onClick={() => setCases(Math.max(1, cases - 1))} className="px-2.5 hover:bg-muted transition-colors" data-testid={`qty-minus-${product.id}`} aria-label="decrease">
                                <Minus size={14} />
                            </button>
                            <input
                                type="number"
                                min="1"
                                value={cases}
                                onChange={(e) => setCases(Math.max(1, parseInt(e.target.value) || 1))}
                                className="w-12 text-center text-sm font-semibold bg-transparent focus:outline-none"
                                data-testid={`qty-input-${product.id}`}
                            />
                            <button onClick={() => setCases(cases + 1)} className="px-2.5 hover:bg-muted transition-colors" data-testid={`qty-plus-${product.id}`} aria-label="increase">
                                <Plus size={14} />
                            </button>
                        </div>
                        <button
                            onClick={handleAdd}
                            disabled={!product.in_stock}
                            data-testid={`add-to-order-${product.id}`}
                            className="flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground text-sm font-semibold py-2.5 hover:bg-secondary hover:text-secondary-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {added ? <><Check size={16} /> Added</> : <><ShoppingCart size={16} /> Add to Order Request</>}
                        </button>
                    </div>
                    <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">Quantity in cases · No online payment</span>
                </div>
            </div>
        </div>
    );
};
